import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScannerOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import productsApi from '../../../api/productsApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import { categoryIndentSx, flattenCategoryTree } from '../../../utils/categoryTree';
import ProductFormDialog from '../../../sections/admin/products/ProductFormDialog';
import BarcodeScannerDialog from '../../../sections/admin/products/BarcodeScannerDialog';
import useAuth from '../../../auth/useAuth';
import { ROLES } from '../../../config';

const currencyPen = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });
const currencyUsd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export default function ProductsListPage() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMINISTRADOR;

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [categoriesRes, brandsRes] = await Promise.all([productsApi.getCategories(), productsApi.getBrands()]);
        setCategories(categoriesRes.data.data);
        setBrands(brandsRes.data.data);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar las categorías y marcas.'), {
          variant: 'error',
        });
      }
    };

    loadFilters();
  }, [enqueueSnackbar]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await productsApi.getProducts({
        search: search || undefined,
        category_id: categoryFilter || undefined,
        brand_id: brandFilter || undefined,
        is_active: statusFilter || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de productos.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, brandFilter, statusFilter, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleNew = () => {
    setEditingProduct(null);
    setFormOpen(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormOpen(true);
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    fetchProducts();
  };

  // Al escanear desde la lista: si el codigo pertenece a un producto lo abre para editar;
  // si no existe, el codigo de barras ya no se puede cargar a mano, asi que solo se avisa.
  const handleBarcodeScanned = async (code) => {
    setScannerOpen(false);
    try {
      const { data } = await productsApi.getProductByBarcode(code);
      enqueueSnackbar('Producto ya registrado, abriendo para editar.', { variant: 'info' });
      setEditingProduct(data.data);
      setFormOpen(true);
    } catch (error) {
      if (error?.response?.status === 404) {
        enqueueSnackbar('Código no registrado a ningún producto.', { variant: 'warning' });
      } else {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo verificar el código escaneado.'), { variant: 'error' });
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await productsApi.deleteProduct(productToDelete.id);
      enqueueSnackbar('Producto eliminado correctamente.', { variant: 'success' });
      setProductToDelete(null);
      fetchProducts();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar el producto.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'image',
        headerName: '',
        width: 64,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const primary = params.row.images?.find((image) => image.is_primary) || params.row.images?.[0];
          return (
            <Avatar
              variant="rounded"
              src={primary?.url}
              sx={{ width: 36, height: 36, bgcolor: 'action.hover', color: 'text.disabled' }}
            >
              <ImageOutlinedIcon fontSize="small" />
            </Avatar>
          );
        },
      },
      { field: 'sku', headerName: 'SKU', width: 140 },
      { field: 'name', headerName: 'Nombre', flex: 1, minWidth: isMobile ? 140 : 220 },
      {
        field: 'category',
        headerName: 'Categoría',
        width: 160,
        valueGetter: (value, row) =>
          row.categories?.length ? row.categories.map((category) => category.name).join(', ') : row.category?.name || '—',
      },
      { field: 'stock_quantity', headerName: 'Stock', width: 90, type: 'number' },
      ...(isAdmin
        ? [
            {
              field: 'cost',
              headerName: 'Costo (US$)',
              width: 130,
              valueFormatter: (value) => (value != null ? currencyUsd.format(value) : '—'),
            },
          ]
        : []),
      {
        field: 'price_usd',
        headerName: 'Precio (US$)',
        width: 130,
        valueFormatter: (value) => currencyUsd.format(value || 0),
      },
      {
        field: 'price_pen',
        headerName: 'Precio (S/)',
        width: 130,
        valueFormatter: (value) => currencyPen.format(value || 0),
      },
      {
        field: 'is_active',
        headerName: 'Estado',
        width: 110,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Activo' : 'Inactivo'}
            color={params.value ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => handleEdit(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setProductToDelete(params.row)}
          />,
        ],
      },
    ],
    [isMobile, isAdmin]
  );

  // En pantallas chicas se ocultan las columnas menos criticas para que la tabla
  // entre sin scroll horizontal excesivo; siguen disponibles al editar el producto.
  const columnVisibilityModel = useMemo(
    () => ({
      sku: false,
      category: !isMobile,
      price_usd: !isMobile,
      cost: !isMobile,
    }),
    [isMobile]
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: { xs: 'stretch', sm: 'flex-start' }, justifyContent: 'space-between' }}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
      >
        <div>
          <Typography variant="h4">Productos</Typography>
          <Typography variant="body2" color="text.secondary">
            CRUD de productos con sus atributos y lectura de código de barras.
          </Typography>
        </div>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            fullWidth={isMobile}
            variant="outlined"
            startIcon={<QrCodeScannerIcon />}
            onClick={() => setScannerOpen(true)}
          >
            Escanear código
          </Button>
          <Button fullWidth={isMobile} variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
            Nuevo producto
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Nombre, SKU, código de barras o serie"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2.5 }}>
            <TextField
              select
              fullWidth
              label="Categoría"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todas</MenuItem>
              {flattenCategoryTree(categories).map((category) => (
                <MenuItem key={category.id} value={category.id} sx={categoryIndentSx(category.depth)}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2.5 }}>
            <TextField
              select
              fullWidth
              label="Marca"
              value={brandFilter}
              onChange={(e) => {
                setBrandFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todas</MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="1">Activo</MenuItem>
              <MenuItem value="0">Inactivo</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ width: '100%', overflowX: 'auto' }}>
        <div className={'dashTblResp'}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            loading={loading}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
          />
        </div>
      </Card>

      <ProductFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleFormSaved}
        product={editingProduct}
        categories={categories}
        brands={brands}
        onImagesChanged={fetchProducts}
      />

      <BarcodeScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={handleBarcodeScanned}
      />

      <Dialog open={Boolean(productToDelete)} onClose={() => setProductToDelete(null)}>
        <DialogTitle>Eliminar producto</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar <strong>{productToDelete?.name}</strong>? Esta acción se puede revertir solo
            desde la base de datos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
